import React from 'react';
import { connect } from 'react-redux';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Table } from 'edenred-ui';

import { DownloadReceiptButton } from '@epi-components/DownloadReceiptButton/DownloadReceiptButton';
import { SortingButton } from '@epi-components/SortingButton';
import { getApplicationLocale } from '@epi-store/application/application.selectors';
import { DocumentSourceTypes } from '@epi-constants/documentSourceTypes';
import { TransactionsHistoryTableProps } from '@epi-models/components/TransactionsHistoryTable';

import { momentDateFormat } from '../../constants/dateFormats';
import { formatCurrencyBalance } from '../../helpers/numeral';
import { TransactionsHistorySorting } from '../../constants/transactionsHistorySorting';

import './TransactionsHistoryTable.scss';

const TransactionsHistoryTable = ({
  isLoading,
  sortBy,
  sortDirection,
  onSortingChange,
  transactions
}: TransactionsHistoryTableProps) => {
  const { t } = useTranslation();

  const loadingContent = (
    <Table.Row className="loading text-center">
      <Table.Cell colSpan={7}>
        <LoadingSpinner />
      </Table.Cell>
    </Table.Row>
  );

  const noContent = () => (
    <Table.Row className="no-content text-center">
      <Table.Cell colSpan={7}>
        {t('containers.transactions_history.no_data')}
      </Table.Cell>
    </Table.Row>
  );

  const getHistoryContent = transactions => {
    const transactionRows = transactions.map(row => [
      <Table.Row className="standard-row" key={`standard${row.id}`}>
        <Table.Cell>{Moment(row.date).format(momentDateFormat)}</Table.Cell>
        <Table.Cell>
          {row.title}
          <br />
          <span className="hidden-xs subtitle">{row.description}</span>
        </Table.Cell>
        <Table.Cell>
          <div className="pull-right">
            {row.isDeposit && (
              <DownloadReceiptButton
                sourceId={row.orderId || row.id}
                sourceType={
                  row.orderId !== null && row.orderId > 0
                    ? DocumentSourceTypes.Order
                    : DocumentSourceTypes.Transaction
                }
              >
                <span>{t('containers.transactions_history.receipt')}</span>
              </DownloadReceiptButton>
            )}
          </div>
        </Table.Cell>
        <Table.Cell className="centered">
          {formatCurrencyBalance(row.balanceChange)}
        </Table.Cell>
      </Table.Row>
    ]);
    return transactionRows;
  };

  const getContent = () => {
    if (isLoading) {
      return loadingContent;
    }

    if (!transactions || transactions.length === 0) {
      return noContent();
    }
    return getHistoryContent(transactions);
  };

  return (
    <div className="TransactionsHistoryTable" id="transactions-history-table">
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Cell>
              <SortingButton
                id="transactions-history-sort-by-date-btn"
                disabled={isLoading}
                sortBy={TransactionsHistorySorting.ByDate}
                sortedBy={sortBy}
                sortDirection={sortDirection}
                onSortingChange={onSortingChange}
                text={t('containers.transactions_history.date_col')}
              />
            </Table.Cell>
            <Table.Cell>
              <SortingButton
                id="transactions-history-sort-by-description-btn"
                disabled={isLoading}
                sortBy={TransactionsHistorySorting.ByDescription}
                sortedBy={sortBy}
                sortDirection={sortDirection}
                onSortingChange={onSortingChange}
                text={t('containers.transactions_history.transaction')}
              />
            </Table.Cell>
            <Table.Cell>&nbsp;</Table.Cell>
            <Table.Cell className="centered">
              <SortingButton
                id="transactions-history-sort-by-amount-btn"
                disabled={isLoading}
                sortBy={TransactionsHistorySorting.ByAmount}
                sortedBy={sortBy}
                sortDirection={sortDirection}
                onSortingChange={onSortingChange}
                text={t('containers.transactions_history.amount')}
              />
            </Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>{getContent()}</Table.Body>
      </Table>
    </div>
  );
};

const mapStateToProps = state => ({
  locale: getApplicationLocale(state)
});

const TransactionsHistoryTableWithI18n = connect(mapStateToProps)(
  TransactionsHistoryTable
);

export { TransactionsHistoryTableWithI18n as TransactionsHistoryTable };
